import { useEffect, useState, useCallback } from "react";
import { Icon, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { fmtDate, fmtRelative } from "../../lib/dates.js";
import { formatMoney, paiseToRupees, rupeesToPaise } from "../../lib/money.js";
import TableSkeleton from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";
import InvoiceDetailModal from "../../components/admin/InvoiceDetailModal.jsx";
import {
  listInvoices,
  listCompanies,
  markInvoicePaid,
  voidInvoice,
  refundInvoice,
  createManualInvoice,
  itemsOf,
} from "../../lib/adminApi";

const PAGE_SIZE = 30;

const TABS = [
  ["all", "All"],
  ["paid", "Paid"],
  ["unpaid", "Unpaid"],
  ["partially_refunded", "Partial refund"],
  ["refunded", "Refunded"],
  ["voided", "Voided"],
];

const STATUS_LABEL = {
  paid: "Paid",
  unpaid: "Unpaid",
  voided: "Voided",
  partially_refunded: "Partial refund",
  refunded: "Refunded",
};

const statusBadge = (s) =>
  s === "paid"     ? "badge-success" :
  s === "unpaid"   ? "badge-warning" :
  s === "voided"   ? "badge-muted"   :
  s === "refunded" ? "badge-info"    :
  s === "partially_refunded" ? "badge-info" : "badge-muted";

function CompanyPicker({ onSelect, current }) {
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setOptions([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await listCompanies(`?search=${encodeURIComponent(q)}&limit=10`);
        if (!cancelled) setOptions(itemsOf(r).items);
      } catch {
        if (!cancelled) setOptions([]);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  return (
    <div style={{ position: "relative" }}>
      <input
        className="input"
        placeholder="Search company by name…"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {current && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
          Selected: <strong>{current.name}</strong> ({current.id.slice(0, 8)})
        </div>
      )}
      {open && options.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%", left: 0, right: 0,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            marginTop: 4,
            zIndex: 20,
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          {options.map((c) => (
            <div
              key={c.id}
              onClick={() => { onSelect(c); setOpen(false); setQ(c.name); }}
              style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid var(--border)" }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{c.slug}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BillingInner() {
  const { toast, openModal, closeModal } = useApp();
  const [resp, setResp] = useState({ items: [], total: 0, hasMore: false, page: 1 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (tab !== "all") params.set("status", tab);
    params.set("page", String(page));
    params.set("limit", String(PAGE_SIZE));
    try {
      setResp(itemsOf(await listInvoices(`?${params}`)));
    } catch (e) {
      toast(e.message || "Failed to load invoices", "error");
    } finally {
      setLoading(false);
    }
  }, [tab, page, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [tab]);

  const visibleInvoices = resp.items.filter((inv) => {
    if (!dateFrom && !dateTo) return true;
    const d = (inv.createdAt || "").slice(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(resp.total / PAGE_SIZE));

  const onManual = () => {
    let company = null;
    let amountRupees = 0;
    let reason = "";
    let periodStart = new Date().toISOString().slice(0, 10);
    let periodEnd = new Date(Date.now() + 30 * 86400 * 1000).toISOString().slice(0, 10);

    openModal(
      <>
        <div className="modal-head">
          <h3>Manual invoice</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>Company</label>
            <CompanyPicker onSelect={(c) => { company = c; }} current={company} />
          </div>
          <div className="field">
            <label>Amount (₹)</label>
            <input
              className="input"
              type="number"
              min={0}
              step="0.01"
              onChange={(e) => { amountRupees = Number(e.target.value); }}
            />
          </div>
          <div className="form-row">
            <div className="field">
              <label>Period start</label>
              <input
                className="input"
                type="date"
                defaultValue={periodStart}
                onChange={(e) => { periodStart = e.target.value; }}
              />
            </div>
            <div className="field">
              <label>Period end</label>
              <input
                className="input"
                type="date"
                defaultValue={periodEnd}
                onChange={(e) => { periodEnd = e.target.value; }}
              />
            </div>
          </div>
          <div className="field">
            <label>Reason / note</label>
            <textarea
              className="input"
              rows={3}
              onChange={(e) => { reason = e.target.value; }}
            />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={async () => {
            if (!company?.id) return toast("Select a company", "error");
            const amountPaise = rupeesToPaise(amountRupees);
            if (amountPaise <= 0) return toast("Amount must be > 0", "error");
            if (!periodEnd || !periodStart || periodEnd <= periodStart) return toast("Invalid period", "error");
            try {
              await createManualInvoice({
                companyId: company.id,
                amount: amountPaise,
                reason,
                periodStart,
                periodEnd,
              });
              toast("Manual invoice created", "success");
              closeModal();
              load();
            } catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="plus" size={14} /> Create
          </button>
        </div>
      </>,
    );
  };

  const onView = (inv) => {
    openModal(<InvoiceDetailModal invoiceId={inv.id} onClose={closeModal} />, "lg");
  };

  const onRefund = (inv) => {
    const refunded = inv.refundedAmount || 0;
    const available = Math.max(0, inv.amount - refunded);
    let amountRupees = paiseToRupees(available);
    let reason = "";
    openModal(
      <>
        <div className="modal-head">
          <h3>Refund {formatMoney(available, inv.currency || "INR")}</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Refund <strong>{inv.company?.name}</strong> invoice {inv.id.slice(0, 8)}.
          </p>
          <div className="field">
            <label>Refund amount (₹)</label>
            <input
              className="input"
              type="number"
              min={0}
              step="0.01"
              defaultValue={amountRupees}
              onChange={(e) => { amountRupees = Number(e.target.value); }}
            />
          </div>
          <div className="field">
            <label>Reason (≥4 chars)</label>
            <textarea className="input" rows={3} onChange={(e) => { reason = e.target.value; }} />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={async () => {
              if (!reason || reason.trim().length < 4) return toast("Reason required (≥4 chars)", "error");
              const amountPaise = rupeesToPaise(amountRupees);
              if (amountPaise <= 0 || amountPaise > available) return toast("Invalid refund amount", "error");
              try {
                const idem = crypto.randomUUID();
                await refundInvoice(inv.id, amountPaise, reason, idem);
                toast("Refund issued", "success");
                closeModal();
                load();
              } catch (e) { toast(e.message, "error"); }
            }}
          >
            <Icon name="corner-down-left" size={14} /> Refund
          </button>
        </div>
      </>,
    );
  };

  const onMarkPaid = (inv) => {
    openModal(
      <>
        <div className="modal-head">
          <h3>Mark invoice paid?</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Invoice {inv.id.slice(0, 8)} for <strong>{inv.company?.name}</strong>. Only use this when payment was received outside Razorpay.
          </p>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={async () => {
            try { await markInvoicePaid(inv.id); toast("Marked paid", "success"); closeModal(); load(); }
            catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="check-circle" size={14} /> Mark Paid
          </button>
        </div>
      </>,
    );
  };

  const onVoid = (inv) => {
    openModal(
      <>
        <div className="modal-head">
          <h3 style={{ color: "var(--error)" }}>Void invoice?</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Voiding is irreversible. Use refund for paid invoices instead.
          </p>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn" style={{ background: "var(--error)", color: "#fff" }} onClick={async () => {
            try { await voidInvoice(inv.id); toast("Voided", "success"); closeModal(); load(); }
            catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="x-circle" size={14} /> Void
          </button>
        </div>
      </>,
    );
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Billing & Invoices</h1>
          <p className="page-sub">{resp.total} invoices · showing {visibleInvoices.length}</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={onManual}>
          <Icon name="plus" size={14} /> Manual Invoice
        </button>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {TABS.map(([k, l]) => (
            <button
              key={k}
              className={`filter-chip ${tab === k ? "active" : ""}`}
              onClick={() => setTab(k)}
            >
              {l}
            </button>
          ))}
          <span style={{ color: "var(--text-muted)", fontSize: 12, marginLeft: 8 }}>Date:</span>
          <input type="date" value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }} />
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>to</span>
          <input type="date" value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }} />
          {(dateFrom || dateTo) && (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => { setDateFrom(""); setDateTo(""); }}>Clear</button>
          )}
        </div>
      </div>

      {loading ? (
        <TableSkeleton columns={7} rows={6} />
      ) : visibleInvoices.length === 0 ? (
        <EmptyState title="No invoices" desc="Nothing to show for this range." icon="receipt" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Company</th>
                <th>Amount</th>
                <th>Refunded</th>
                <th>Status</th>
                <th>Period</th>
                <th>Created</th>
                <th style={{ width: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleInvoices.map((inv) => {
                const refunded = inv.refundedAmount || 0;
                return (
                  <tr key={inv.id}>
                    <td data-label="Invoice" style={{ fontFamily: "monospace", fontSize: 12 }}>
                      {inv.id.slice(0, 8)}
                    </td>
                    <td data-label="Company">
                      <div style={{ fontWeight: 600 }}>{inv.company?.name || "—"}</div>
                    </td>
                    <td data-label="Amount"><strong>{formatMoney(inv.amount, inv.currency || "INR")}</strong></td>
                    <td data-label="Refunded">
                      {refunded > 0 ? (
                        <strong style={{ color: "var(--info)" }}>{formatMoney(refunded, inv.currency || "INR")}</strong>
                      ) : (
                        <span style={{ color: "var(--text-muted)" }}>—</span>
                      )}
                    </td>
                    <td data-label="Status">
                      <span className={`badge ${statusBadge(inv.status)}`}>
                        <span className="badge-dot" /> {STATUS_LABEL[inv.status] || inv.status}
                      </span>
                    </td>
                    <td data-label="Period" style={{ fontSize: 12 }}>
                      {fmtDate(inv.periodStart)} — {fmtDate(inv.periodEnd)}
                    </td>
                    <td data-label="Created" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {fmtRelative(inv.createdAt)}
                    </td>
                    <td data-label="">
                      <div className="row-actions">
                        <button className="icon-btn" title="View" onClick={() => onView(inv)}>
                          <Icon name="eye" size={14} />
                        </button>
                        {inv.status === "unpaid" && (
                          <button className="icon-btn" title="Mark Paid" onClick={() => onMarkPaid(inv)}>
                            <Icon name="check-circle" size={14} />
                          </button>
                        )}
                        {(inv.status === "paid" || inv.status === "partially_refunded") && (inv.amount - refunded) > 0 && (
                          <button className="icon-btn" title="Refund" onClick={() => onRefund(inv)}>
                            <Icon name="corner-down-left" size={14} />
                          </button>
                        )}
                        {inv.status === "unpaid" && (
                          <button
                            className="icon-btn"
                            title="Void"
                            style={{ color: "var(--error)" }}
                            onClick={() => onVoid(inv)}
                          >
                            <Icon name="x-circle" size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && resp.total > 0 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 16, alignItems: "center" }}>
          <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <Icon name="chevron-left" size={14} /> Prev
          </button>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Page {page} of {totalPages}
          </span>
          <button className="btn btn-ghost btn-sm" disabled={!resp.hasMore} onClick={() => setPage((p) => p + 1)}>
            Next <Icon name="chevron-right" size={14} />
          </button>
        </div>
      )}
    </section>
  );
}

export default function AdminBilling() {
  return (
    <AdminPageBoundary>
      <BillingInner />
    </AdminPageBoundary>
  );
}
